import { useQuery } from '@tanstack/react-query';
import { pb } from '@/lib/pocketbase';

export const useBatchReviews = (tutorIds: string[]) => {
  const query = useQuery({
    queryKey: ['batch-reviews', tutorIds.join(',')],
    queryFn: async () => {
      if (tutorIds.length === 0) return {};

      try {
        // Batch fetch all reviews for all tutors at once
        const allReviews = await pb.collection('reviews').getFullList({
          filter: tutorIds.map(id => `tutor="${id}"`).join(' || '),
          fields: 'tutor,rating'
        });

        // Group reviews by tutor and calculate stats
        const reviewStats: Record<string, { average: number; count: number }> = {};
        
        tutorIds.forEach(tutorId => {
          const tutorReviews = allReviews.filter(r => r.tutor === tutorId);
          const count = tutorReviews.length;
          const average = count > 0 
            ? tutorReviews.reduce((sum, r) => sum + r.rating, 0) / count 
            : 5.0;
          
          reviewStats[tutorId] = {
            average: Number(average.toFixed(1)),
            count
          };
        });

        return reviewStats;
      } catch (error: any) {
        if (error.status !== 0 && error.status !== 403 && error.status !== 401) {
          console.error('Error fetching batch reviews:', error);
        }
        // Return default 5.0 ratings for all tutors on error
        return tutorIds.reduce((acc, id) => {
          acc[id] = { average: 5.0, count: 0 };
          return acc;
        }, {} as Record<string, { average: number; count: number }>);
      }
    },
    enabled: tutorIds.length > 0,
    refetchOnWindowFocus: false,
    staleTime: 60000, // Cache for 1 minute
  });

  return {
    reviewStats: query.data || {},
    isLoading: query.isLoading,
  };
};
